import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Shield, Smartphone, Copy, Check, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/App";
import { API } from "@/config";
import { toast } from "sonner";

/**
 * TwoFactorDialog - Handles TOTP 2FA setup and verification
 * 
 * Props:
 * - open: boolean
 * - onClose: function
 * - userId: string
 * - email: string
 * - onSuccess: function(userData)
 * - mode: "setup" | "verify" (setup shows QR code, verify just asks for code)
 * - qrCode: string (base64 QR code for setup mode)
 * - totpSecret: string (for manual entry in setup mode)
 * - tempToken: string (for verify mode)
 */
export default function TwoFactorDialog({ 
  open, 
  onClose, 
  userId, 
  email,
  onSuccess,
  mode = "verify",
  qrCode = null,
  totpSecret = null,
  tempToken = null
}) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRefs = useRef([]);
  const { login } = useAuth();

  const isSetupMode = mode === "setup" || qrCode;

  // Auto-focus first input when dialog opens
  useEffect(() => {
    if (open && inputRefs.current[0]) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [open]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setCode(['', '', '', '', '', '']);
      setShowSecret(false);
      setCopied(false);
    }
  }, [open]);

  const handleChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerify(fullCode);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      const fullCode = code.join('');
      if (fullCode.length === 6) {
        handleVerify(fullCode);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < pasted.length && i < 6; i++) {
        newCode[i] = pasted[i];
      }
      setCode(newCode);
      
      if (pasted.length === 6) {
        handleVerify(pasted);
      } else {
        inputRefs.current[pasted.length]?.focus();
      }
    }
  };

  const handleVerify = async (codeValue) => {
    if (!codeValue || codeValue.length !== 6) return;
    
    setLoading(true);
    try {
      // Different endpoint for setup vs verify
      const endpoint = isSetupMode ? '/auth/setup-2fa' : '/auth/verify-2fa';
      const body = isSetupMode 
        ? { user_id: userId, code: codeValue }
        : { user_id: userId, code: codeValue, temp_token: tempToken };

      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('session_token', data.session_token);
        login(data);
        
        if (isSetupMode) {
          toast.success("2FA attivato con successo!");
        } else {
          toast.success("Accesso effettuato!");
        }
        
        onSuccess?.(data);
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Codice non valido");
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error("2FA error:", error);
      toast.error("Errore di verifica");
    } finally {
      setLoading(false);
    }
  };

  const copySecret = async () => {
    if (totpSecret) {
      await navigator.clipboard.writeText(totpSecret);
      setCopied(true);
      toast.success("Codice copiato!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setCode(['', '', '', '', '', '']);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[420px] rounded-3xl bg-white dark:bg-[#1C1C1E] p-0 border-0 overflow-hidden">
        <div className="p-6">
          <DialogHeader className="text-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center mx-auto mb-4">
              <Shield className="text-white" size={32} />
            </div>
            <DialogTitle className="text-2xl font-bold text-[#1D1D1F] dark:text-white">
              {isSetupMode ? "Configura 2FA" : "Verifica identità"}
            </DialogTitle>
            <p className="text-[#86868B] mt-2">
              {isSetupMode 
                ? "Scansiona il QR code con la tua app di autenticazione"
                : "Inserisci il codice dalla tua app di autenticazione"
              }
            </p>
          </DialogHeader>

          {/* QR Code for setup mode */}
          {isSetupMode && qrCode && (
            <div className="mb-6">
              {/* QR Code Image */}
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-white rounded-2xl shadow-sm border border-gray-100">
                  <img 
                    src={qrCode} 
                    alt="QR Code per 2FA" 
                    className="w-48 h-48"
                  />
                </div>
              </div>

              {/* Manual entry option */}
              <div className="bg-[#F5F5F7] dark:bg-white/5 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#86868B]">Oppure inserisci manualmente:</span>
                  <button
                    onClick={() => setShowSecret(!showSecret)}
                    className="text-[#FF6B4A] hover:text-[#E05A3A]"
                  >
                    {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono bg-white dark:bg-white/10 p-2 rounded-lg overflow-x-auto">
                    {showSecret ? totpSecret : '••••••••••••••••••••••••••••••••'}
                  </code>
                  <button
                    onClick={copySecret}
                    className="p-2 hover:bg-white dark:hover:bg-white/10 rounded-lg transition-colors"
                    title="Copia codice"
                  >
                    {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} className="text-[#86868B]" />}
                  </button>
                </div>
              </div>

              {/* App suggestions */}
              <div className="mt-4 flex items-center justify-center gap-4 text-xs text-[#86868B]">
                <span className="flex items-center gap-1">
                  <Smartphone size={14} />
                  Google Authenticator
                </span>
                <span>•</span>
                <span>Authy</span>
                <span>•</span>
                <span>Microsoft Authenticator</span>
              </div>
            </div>
          )}

          {/* Code Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#1D1D1F] dark:text-white mb-2 text-center">
              {isSetupMode ? "Inserisci il codice per confermare:" : "Codice a 6 cifre"}
            </label>
            <div className="flex justify-center gap-2" onPaste={handlePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onFocus={(e) => e.target.select()}
                  className="w-12 h-14 text-center text-2xl font-bold rounded-xl bg-[#F5F5F7] dark:bg-white/10 border-0 focus:ring-2 focus:ring-[#FF6B4A] focus:outline-none"
                  disabled={loading}
                  autoComplete="one-time-code"
                  data-testid={`2fa-input-${index}`}
                />
              ))}
            </div>
          </div>

          {/* Verify Button */}
          <Button
            onClick={() => handleVerify(code.join(''))}
            disabled={loading || code.join('').length !== 6}
            className="w-full h-14 rounded-xl bg-[#FF6B4A] hover:bg-[#E05A3A] text-white text-lg font-semibold"
            data-testid="2fa-verify-button"
          >
            {loading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              isSetupMode ? "Attiva 2FA" : "Verifica"
            )}
          </Button>

          {/* Back Button */}
          <button
            onClick={handleClose}
            className="mt-6 w-full text-center text-[#86868B] hover:text-[#1D1D1F] dark:hover:text-white flex items-center justify-center gap-2"
          >
            <ArrowLeft size={16} />
            {isSetupMode ? "Annulla" : "Torna al login"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
